// Per-carrier fleet sub-card (D3j). Renders inline under a carrier
// display row when the user clicks its "Rigs" toggle in
// LogisticsCarriersList. Sibling to LogisticsCarriersRouteHistoryCard
// (route history) -- the two share the single-open-at-a-time pattern.
//
// Affordances: per-row inline edit + per-row toggle-active (matches
// the carrier-row pattern from LogisticsCarriersDisplayRow), inline
// add-rig form at the top. The capacity inputs are a value + unit
// pair (set together or both blank); the form short-circuits the
// pair rule before the round-trip so the user sees a clean message
// instead of a 400.
import { Fragment } from "react";
import {
  ghostBtnClass,
  inputClass,
  primaryBtnClass,
} from "./logisticsCarriersShared";
import {
  RIG_CAPACITY_UNIT_LABELS,
  RIG_CAPACITY_UNITS,
  useLogisticsCarrierFleet,
} from "./useLogisticsCarrierFleet";
import type { LogiCarrier, LogiCarrierRig, LogiRigCapacityUnit } from "@/lib/logistics";

type Props = {
  carrier: LogiCarrier;
  f: ReturnType<typeof useLogisticsCarrierFleet>;
  onClose: () => void;
};

export function LogisticsCarriersFleetCard({ carrier, f, onClose }: Props) {
  return (
    <div className="mt-2 rounded-xl border border-emerald-200 bg-emerald-50/40 dark:border-emerald-800 dark:bg-emerald-950/20 p-3">
      <div className="flex flex-wrap items-center gap-2 justify-between mb-2">
        <div className="text-xs font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-300">
          Fleet for {carrier.name}
          {f.rigs.length > 0 ? (
            <span className="ml-2 font-normal text-gray-500 dark:text-zinc-400">
              ({f.rigs.length} {f.rigs.length === 1 ? "rig" : "rigs"})
            </span>
          ) : null}
        </div>
        <div className="flex gap-2">
          {!f.adding ? (
            <button
              type="button"
              className={primaryBtnClass}
              onClick={f.startAdd}
              disabled={f.actionBusy}
            >
              + Add rig
            </button>
          ) : null}
          <button
            type="button"
            className={ghostBtnClass}
            onClick={() => void f.reload()}
            disabled={f.loading || f.actionBusy}
          >
            {f.loading ? "Loading…" : "Refresh"}
          </button>
          <button type="button" className={ghostBtnClass} onClick={onClose}>
            Close
          </button>
        </div>
      </div>

      {f.error ? (
        <div className="mb-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-900 dark:border-red-800 dark:bg-red-950/35 dark:text-red-100">
          {f.error}
        </div>
      ) : null}

      {f.adding ? <RigDraftForm f={f} mode="add" /> : null}

      {!f.error && f.loading && f.rigs.length === 0 ? (
        <div className="text-sm text-gray-500 dark:text-zinc-400">Loading rigs…</div>
      ) : null}
      {!f.error && !f.loading && f.rigs.length === 0 && !f.adding ? (
        <div className="text-sm text-gray-500 dark:text-zinc-400">
          No rigs yet for this carrier. Use + Add rig above to record the first one. A rig
          can be as simple as a designation (&ldquo;Mark&apos;s Trucking rig 1&rdquo;, &ldquo;Chris
          Johnson&apos;s rig&rdquo;, &ldquo;DOT29384058&rdquo;, &ldquo;Mark&apos;s 60&apos; rig&rdquo;); driver and capacity
          are optional.
        </div>
      ) : null}

      {f.rigs.length > 0 ? (
        <ul className="divide-y divide-emerald-100 dark:divide-emerald-900/40">
          {f.rigs.map((rig) => (
            <Fragment key={rig.id}>
              {f.editingId === rig.id ? (
                <RigEditRow rig={rig} f={f} />
              ) : (
                <RigDisplayRow rig={rig} f={f} />
              )}
            </Fragment>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function RigDisplayRow({
  rig,
  f,
}: {
  rig: LogiCarrierRig;
  f: ReturnType<typeof useLogisticsCarrierFleet>;
}) {
  const capacity =
    rig.capacity_value !== null && rig.capacity_unit
      ? `${rig.capacity_value} ${RIG_CAPACITY_UNIT_LABELS[rig.capacity_unit]}`
      : null;
  return (
    <li className="py-2 flex flex-wrap items-start gap-2">
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2 text-sm font-medium text-gray-900 dark:text-zinc-100">
          {rig.designation}
          {!rig.is_active ? (
            <span className="rounded-md bg-gray-400 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
              Parked
            </span>
          ) : null}
        </div>
        {rig.standard_driver_name ? (
          <div className="text-xs text-gray-600 dark:text-zinc-400">
            Standard driver: {rig.standard_driver_name}
          </div>
        ) : (
          <div className="text-xs text-gray-500 italic dark:text-zinc-500">
            No standard driver (rotating; fill in at scheduling)
          </div>
        )}
        {capacity ? (
          <div className="text-xs text-gray-600 dark:text-zinc-400">Capacity: {capacity}</div>
        ) : null}
        {rig.notes ? (
          <div className="text-xs text-gray-600 dark:text-zinc-400">{rig.notes}</div>
        ) : null}
      </div>
      <div className="flex gap-2">
        <button
          type="button"
          className={ghostBtnClass}
          onClick={() => f.startEdit(rig)}
          disabled={f.actionBusy}
        >
          Edit
        </button>
        <button
          type="button"
          className={ghostBtnClass}
          onClick={() => void f.toggleActive(rig)}
          disabled={f.actionBusy}
        >
          {rig.is_active ? "Park" : "Reactivate"}
        </button>
      </div>
    </li>
  );
}

function RigEditRow({
  rig,
  f,
}: {
  rig: LogiCarrierRig;
  f: ReturnType<typeof useLogisticsCarrierFleet>;
}) {
  return (
    <li className="py-3">
      <div className="text-xs text-gray-500 dark:text-zinc-500 mb-1">
        Editing rig <code>{rig.id.slice(0, 8)}</code>
      </div>
      <RigDraftForm f={f} mode="edit" />
    </li>
  );
}

// Shared form for both add and edit modes. Differences are small enough
// that one component with a mode flag is cleaner than two parallel forms.
function RigDraftForm({
  f,
  mode,
}: {
  f: ReturnType<typeof useLogisticsCarrierFleet>;
  mode: "add" | "edit";
}) {
  const draft = mode === "add" ? f.draft : f.edit;
  const setDraft = mode === "add" ? f.setDraft : f.setEdit;
  const submit = mode === "add" ? f.submitAdd : f.submitEdit;
  const cancel = mode === "add" ? f.cancelAdd : f.cancelEdit;
  return (
    <div className="space-y-2 rounded-lg border border-emerald-200/60 bg-white/60 p-3 dark:border-emerald-800/60 dark:bg-zinc-900/40 mb-2">
      <label className="block text-xs text-gray-600 dark:text-zinc-300">
        Designation
        <input
          className={`${inputClass} mt-1`}
          value={draft.designation}
          onChange={(e) => setDraft({ ...draft, designation: e.target.value })}
          placeholder="e.g. Mark's Trucking rig 1, Chris Johnson's rig, DOT29384058, Mark's 60' rig"
        />
      </label>
      <label className="block text-xs text-gray-600 dark:text-zinc-300">
        Standard driver (optional — leave blank for rotating drivers)
        <input
          className={`${inputClass} mt-1`}
          value={draft.standard_driver_name}
          onChange={(e) => setDraft({ ...draft, standard_driver_name: e.target.value })}
          placeholder="Owner-operator name, or leave blank if drivers rotate"
        />
      </label>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <label className="block text-xs text-gray-600 dark:text-zinc-300">
          Capacity value (optional)
          <input
            className={`${inputClass} mt-1`}
            value={draft.capacity_value_text}
            onChange={(e) => setDraft({ ...draft, capacity_value_text: e.target.value })}
            inputMode="decimal"
            placeholder="e.g. 60"
          />
        </label>
        <label className="block text-xs text-gray-600 dark:text-zinc-300">
          Capacity unit
          <select
            className={`${inputClass} mt-1`}
            value={draft.capacity_unit}
            onChange={(e) =>
              setDraft({
                ...draft,
                capacity_unit: e.target.value as "" | LogiRigCapacityUnit,
              })
            }
          >
            <option value="">(no unit — leave capacity blank)</option>
            {RIG_CAPACITY_UNITS.map((u) => (
              <option key={u} value={u}>
                {RIG_CAPACITY_UNIT_LABELS[u]}
              </option>
            ))}
          </select>
        </label>
      </div>
      <label className="block text-xs text-gray-600 dark:text-zinc-300">
        Notes (optional)
        <textarea
          className={`${inputClass} mt-1`}
          value={draft.notes}
          onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
          rows={2}
          placeholder="e.g. regular trailer 48' but has a 10' extension at the front that can accommodate an extra 10' of product"
        />
      </label>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          className={primaryBtnClass}
          onClick={() => void submit()}
          disabled={f.actionBusy}
        >
          {f.actionBusy ? "Saving…" : mode === "add" ? "Add rig" : "Save rig"}
        </button>
        <button type="button" className={ghostBtnClass} onClick={cancel} disabled={f.actionBusy}>
          Cancel
        </button>
      </div>
    </div>
  );
}
